import { createContext, useContext } from 'react'

import { getUTMFromPartner, isPreviewMode } from 'helper'
import { sendEvent as sendEventApi } from './api'
import { uidKey } from './constants'
import * as storage from './storage'
import { isAnalyticsAllowed, isAnalyticsConsentManaged, subscribeToAnalyticsConsent } from './consent'

const deferredInitialEvents = new Set(['WidgetViewed', 'RetailerDisplayed'])

if (isAnalyticsConsentManaged() && !isAnalyticsAllowed()) storage.remove(uidKey)
subscribeToAnalyticsConsent(allowed => {
  if (!allowed) storage.remove(uidKey)
})

export const getUTMs = () => {
  const utm = []

  const utmSource = getUTMFromPartner('utm_source')
  const utmCampaign = getUTMFromPartner('utm_campaign')
  const utmMedium = getUTMFromPartner('utm_medium')

  if (utmSource) {
    utm.push(`utm_source=${utmSource}`)
  }
  if (utmMedium) {
    utm.push(`utm_medium=${utmMedium}`)
  }
  if (utmCampaign) {
    utm.push(`utm_campaign=${utmCampaign}`)
  }

  return utm.join('&')
}

const genUUID = () => {
  try {
    return crypto.randomUUID()
  } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }
}

export const EventName = {
  WIDGET_BUTTON_IMPRESSION: 'WidgetImpression',
  WIDGET_VIEWED: 'WidgetViewed',
  RETAILER_DISPLAYED: 'RetailerDisplayed',
  RETAILER_CLICKED: 'RetailerClicked',
}

export const BroadcastEventName = {
  WIDGET_VIEW: 'sidechef.widget_view',
  WIDGET_CLICK: 'sidechef.widget_click',
  ADD_TO_CART: 'sidechef.addToCart',
}

export class Analytics {
  static getOrCreateUUID() {
    if (!isAnalyticsAllowed()) return null
    let uuid = storage.get(uidKey)
    if (!uuid) {
      uuid = genUUID()
      storage.set(uidKey, uuid)
    }
    return uuid
  }

  static getEventPayload(event, attributes, partner) {
    const utmSource = getUTMFromPartner('utm_source') || ''
    const utmCampaign = getUTMFromPartner('utm_campaign') || ''
    const utmMedium = getUTMFromPartner('utm_medium') || ''
    return {
      event_type: event,
      uuid: Analytics.getOrCreateUUID(),
      partner,
      attributes: {
        platform: 'web',
        referrer_name: 'widget',
        referrer: null,
        keyword_list: [],
        zipcode: null,
        name: null,
        name_list: [utmMedium, utmSource, utmCampaign],
        ...attributes,
      },
    }
  }

  constructor(recipeId, recipeUrl, expName = null) {
    this.idList = recipeId ? [recipeId] : null
    this.referrer = recipeUrl
    this.exp = expName
    this.uuid = Analytics.getOrCreateUUID()
    this.pendingInitialEvents = new Map()
    this.sentInitialEvents = new Set()
    this.unsubscribe = subscribeToAnalyticsConsent(allowed => {
      if (!allowed) {
        this.uuid = null
      } else {
        const pendingEvents = [...this.pendingInitialEvents]
        this.pendingInitialEvents.clear()
        pendingEvents.forEach(([event, { payload, partner }]) => this.sendEvent(event, payload, partner))
      }
    })
  }

  getUUID() {
    if (!isAnalyticsAllowed()) return null
    this.uuid = this.uuid || Analytics.getOrCreateUUID()
    return this.uuid
  }

  sendEvent(event, payload, partner) {
    if (!isAnalyticsAllowed()) {
      if (isAnalyticsConsentManaged() && deferredInitialEvents.has(event) && !this.sentInitialEvents.has(event)) {
        this.pendingInitialEvents.set(event, { payload, partner })
      }
      return
    }

    const eventPayload = Analytics.getEventPayload(event, {
      referrer: this.referrer || null,
      keyword_list: this.idList || [],
      name: this.exp || null,
      ...payload,
    }, partner)

    if (isPreviewMode()) return
    sendEventApi(eventPayload)
    if (isAnalyticsConsentManaged() && deferredInitialEvents.has(event)) this.sentInitialEvents.add(event)
  }

  destroy() {
    this.unsubscribe()
    this.pendingInitialEvents.clear()
  }
}

const noopAnalytics = {
  sendEvent: () => {},
  getUUID: () => null,
}

export const AnalyticsContext = createContext(noopAnalytics)
export const useAnalytics = () => useContext(AnalyticsContext) ?? noopAnalytics
